import React, { useState, useEffect } from "react";
import { Dropdown, Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { FaRegLightbulb, FaRegUser, FaSignOutAlt } from "react-icons/fa";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { IoAlertCircleOutline, IoSettingsOutline } from "react-icons/io5";
import { FaRegCalendarAlt } from "react-icons/fa";
import { TbFiles, TbScreenShare } from "react-icons/tb";
import { LuUsers } from "react-icons/lu";
import { PiHandbagSimple } from "react-icons/pi";
import { Link, useNavigate } from "react-router-dom";
import Newidea from "./help/newIdea";
import "../../App.css";
import "./Header.css";
import { useAuth } from "../login/AuthProvider.js";
import NotificationBell from "../NotificationBell.jsx";

function MyVerticallyCenteredModal(props) {
  return (
    <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton className="lab-header__modal-header">
        <Modal.Title id="contained-modal-title-vcenter">Customer Feedback / Issue</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Newidea />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={props.onHide} className="lab-header__modal-btn">
          Submit
        </Button>
        <Button onClick={props.onHide} className="lab-header__modal-btn">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function Header() {
  const [modalShow, setModalShow] = useState(false);
  const navigate = useNavigate();
  const { logout, user, getApiBaseUrl } = useAuth();
  const [userName, setUserName] = useState("Admin SM");
  const [labName, setLabName] = useState("");
  const [allowedPaths, setAllowedPaths] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [userId, setUserId] = useState(null);
  const [technicianStamp, setTechnicianStamp] = useState(null);
  const [stampUrl, setStampUrl] = useState(null);
  const [isLoadingStamp, setIsLoadingStamp] = useState(false);
  const [stampError, setStampError] = useState(false);

  // State for manual update badge
  const [hasManualUpdate, setHasManualUpdate] = useState(false);
  const [manualVersion, setManualVersion] = useState(null);

  // Screen size detection for scaling
  const [scaleFactor, setScaleFactor] = useState(1);

  useEffect(() => {
    const calculateScale = () => {
      const width = window.innerWidth;
      let scale = Math.min(1, width / 1440);
      scale = Math.max(0.3, scale);
      setScaleFactor(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/");
  };

  const checkManualUpdates = async () => {
    try {
      const response = await fetch('https://hms.backend.hiragroupcloud.com/api/manuals');
      const data = await response.json();

      if (data.success && data.data) {
        const currentVersion = data.data.version;
        setManualVersion(currentVersion);

        const storedVersion = localStorage.getItem('manualLastViewedVersion');

        if (!storedVersion || currentVersion > parseInt(storedVersion)) {
          setHasManualUpdate(true);
        } else {
          setHasManualUpdate(false);
        }
      }
    } catch (error) {
      // silent
    }
  };

  const markManualAsViewed = () => {
    if (manualVersion) {
      localStorage.setItem('manualLastViewedVersion', manualVersion);
      localStorage.setItem('manualLastViewedTimestamp', Date.now().toString());
      setHasManualUpdate(false);
    }
  };

  const loadStamp = () => {
    setIsLoadingStamp(true);
    setStampError(false);

    try {
      const storedStamp = localStorage.getItem('technicianStamp');

      if (storedStamp && storedStamp !== 'null' && storedStamp !== 'undefined' && storedStamp.trim() !== '') {
        setTechnicianStamp(storedStamp);
        const baseUrl = getApiBaseUrl ? getApiBaseUrl() : 'https://hms.backend.hiragroupcloud.com';
        const fullUrl = storedStamp.startsWith('/') ? `${baseUrl}${storedStamp}` : `${baseUrl}/${storedStamp}`;
        setStampUrl(fullUrl);
      } else {
        setTechnicianStamp(null);
        setStampUrl(null);
        localStorage.removeItem('technicianStamp');
      }
    } catch (error) {
      setTechnicianStamp(null);
      setStampUrl(null);
      localStorage.removeItem('technicianStamp');
    } finally {
      setIsLoadingStamp(false);
    }
  };

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName");
    const storedUserRole = localStorage.getItem("userRole");
    const storedUserId = localStorage.getItem("userId");

    if (storedUserName) setUserName(storedUserName);
    if (storedUserRole) setUserRole(storedUserRole);
    if (storedUserId) setUserId(storedUserId);

    loadStamp();
    checkManualUpdates();

    const interval = setInterval(checkManualUpdates, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleManualUpdate = () => checkManualUpdates();
    window.addEventListener('manualUpdated', handleManualUpdate);
    return () => window.removeEventListener('manualUpdated', handleManualUpdate);
  }, []);

  useEffect(() => {
    const handleStampUpdate = () => loadStamp();
    window.addEventListener('stampUpdated', handleStampUpdate);
    return () => window.removeEventListener('stampUpdated', handleStampUpdate);
  }, []);

  useEffect(() => {
    if (user) loadStamp();
  }, [user]);

  useEffect(() => {
    try {
      const storedUserData = localStorage.getItem("userData");
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        if (parsedUserData.labName) setLabName(parsedUserData.labName);
      }
    } catch (error) {
      // silent
    }
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (userRole === 'Admin') {
        setAllowedPaths([
          "/support", "/manuals", "/keyboard", "/user",
          "/calender", "/file", "/labSetting", "/users", "/Tabs",
          "/", "/dashboard", "/profile", "/account", "/finance", "/settings",
          "/editUser/:userId"
        ]);
        return;
      }

      if (user && user.userId) {
        try {
          const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${user.userId}/pages`);
          if (!response.ok) throw new Error("Failed to fetch permissions");
          const data = await response.json();
          if (Array.isArray(data)) {
            const paths = data
              .filter(permission => permission && permission.page && permission.isVisible)
              .map(permission => permission.page.path);
            setAllowedPaths(paths);
          } else {
            setAllowedPaths([]);
          }
        } catch (err) {
          setAllowedPaths([]);
        }
      }
    };
    fetchPermissions();
  }, [user, userRole]);

  // FIX: properly match parameterized routes like /editUser/:userId
  const canAccess = (path) => {
    if (userRole === 'Admin') return true;

    // Always allow a user to view/edit their own account
    if (userId && path === `/editUser/${userId}`) return true;

    return allowedPaths.some(allowedPath => {
      // Exact match
      if (allowedPath === path) return true;

      // Parameterized route (e.g. /editUser/:userId)
      if (allowedPath.includes(':')) {
        const allowedSegments = allowedPath.split('/');
        const pathSegments = path.split('/');

        if (allowedSegments.length !== pathSegments.length) return false;

        return allowedSegments.every((seg, i) => {
          if (seg.startsWith(':')) return true;
          return seg === pathSegments[i];
        });
      }

      // Path is a child of an allowed base path
      if (path.startsWith(allowedPath + '/')) return true;

      return false;
    });
  };

  // ✅ Disabled items list
  // "/file" REMOVED so Files tab is now enabled and clickable
  const disabledPaths = ["/support", "/keyboard", "/"];
  const isDisabledPath = (path) => disabledPaths.includes(path);
  const isNewIdeaDisabled = true; // New Idea always disabled

  const getUserInitials = () => {
    if (!userName) return "U";
    const names = userName.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return userName[0].toUpperCase();
  };

  const getRoleBadgeColor = () => {
    switch (userRole) {
      case 'Admin': return '#dc3545';
      case 'Manager': return '#17a2b8';
      case 'Editor': return '#28a745';
      case 'Viewer': return '#6c757d';
      default: return '#004aad';
    }
  };

  const getRoleDisplayName = () => {
    switch (userRole) {
      case 'Admin': return 'Administrator';
      case 'Manager': return 'Manager';
      case 'Editor': return 'Editor';
      case 'Viewer': return 'Viewer';
      default: return userRole || 'User';
    }
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    setStampError(true);
    setTechnicianStamp(null);
    setStampUrl(null);
    localStorage.removeItem('technicianStamp');
  };

  return (
    <div className="lab-header" style={{ '--scale': scaleFactor }}>
      <div className="lab-header__row">
        {/* Left Section - Logo and Stamp */}
        <div className="lab-header__left">
          <Link to="/home" className="lab-header__logo-link">
            <img
              src="/logo.png"
              alt="Lab Logo"
              className="lab-header__logo"
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/180x80?text=Hira+Dental+Lab";
              }}
            />
          </Link>

          {!stampError && isLoadingStamp ? (
            <div className="lab-header__stamp-loading">Loading...</div>
          ) : !stampError && technicianStamp && stampUrl ? (
            <img
              src={stampUrl}
              alt="Technician Stamp"
              className="lab-header__stamp"
              onError={handleImageError}
            />
          ) : null}
        </div>

        {/* Right Section */}
        <div className="lab-header__right">
          <NotificationBell />

          {/* Help Menu */}
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              id="dropdown-basic-help"
              className="lab-header__toggle lab-header__toggle--relative"
            >
              Help
              {hasManualUpdate && <span className="lab-header__badge-dot"></span>}
            </Dropdown.Toggle>
            <Dropdown.Menu className="lab-header__menu">
              {canAccess("/support") && (
                <Dropdown.Item
                  disabled
                  className="lab-header__menu-item lab-header__menu-item--disabled"
                >
                  <AiOutlineQuestionCircle className="lab-header__menu-icon" /> Support
                </Dropdown.Item>
              )}

              {/* Manual - always visible to everyone */}
              <Dropdown.Item
                as={Link}
                to="/manuals"
                className="lab-header__menu-item"
                onClick={markManualAsViewed}
              >
                <IoAlertCircleOutline className="lab-header__menu-icon" /> Manual
                {hasManualUpdate && <span className="lab-header__new-badge">New</span>}
              </Dropdown.Item>

              {canAccess("/keyboard") && (
                <Dropdown.Item
                  disabled
                  className="lab-header__menu-item lab-header__menu-item--disabled"
                >
                  <IoAlertCircleOutline className="lab-header__menu-icon" /> Keyboard Shortcuts
                </Dropdown.Item>
              )}
              <Dropdown.Item
                disabled
                className="lab-header__menu-item lab-header__menu-item--flat lab-header__menu-item--disabled"
              >
                <FaRegLightbulb className="lab-header__menu-icon" /> New Idea
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* User Menu */}
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              id="dropdown-basic-user"
              className="lab-header__toggle lab-header__toggle--user"
            >
              <span className="lab-header__toggle-username">{userName}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="lab-header__menu">
              <div className="lab-header__menu-header">
                <div className="lab-header__menu-header-name">{userName}</div>
                <div
                  className="lab-header__menu-header-role"
                  style={{ color: getRoleBadgeColor() }}
                >
                  {getRoleDisplayName()}
                </div>
              </div>

              {userId && canAccess(`/editUser/${userId}`) && (
                <Dropdown.Item
                  as={Link}
                  to={`/editUser/${userId}`}
                  className="lab-header__menu-item"
                >
                  <FaRegUser className="lab-header__menu-icon" /> My Account
                </Dropdown.Item>
              )}

              <hr className="lab-header__hr" />

              <Dropdown.Item
                onClick={handleLogout}
                className="lab-header__menu-item lab-header__logout"
              >
                <FaSignOutAlt className="lab-header__menu-icon" /> Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Lab Menu */}
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              id="dropdown-basic-lab"
              className="lab-header__toggle"
            >
              @{labName}
            </Dropdown.Toggle>
            <Dropdown.Menu className="lab-header__menu">
              <div className="lab-header__menu-header">
                <div className="lab-header__menu-header-name">{labName}</div>
                <div className="lab-header__menu-header-sub">Dental Laboratory</div>
              </div>

              {canAccess("/calender") && (
                <Dropdown.Item
                  as={Link}
                  to="/calender"
                  className="lab-header__menu-item"
                >
                  <FaRegCalendarAlt className="lab-header__menu-icon" /> Calendar
                </Dropdown.Item>
              )}

              {/* ✅ Files tab now ENABLED — was previously disabled */}
              {canAccess("/file") && (
                <Dropdown.Item
                  as={Link}
                  to="/file"
                  className="lab-header__menu-item"
                >
                  <TbFiles className="lab-header__menu-icon" /> Files
                </Dropdown.Item>
              )}

              {canAccess("/labSetting") && (
                <Dropdown.Item as={Link} to="/labSetting" className="lab-header__menu-item">
                  <IoSettingsOutline className="lab-header__menu-icon" /> Lab Settings
                </Dropdown.Item>
              )}

              <hr className="lab-header__hr" />

              {canAccess("/users") && (
                <Dropdown.Item as={Link} to="/users" className="lab-header__menu-item">
                  <LuUsers className="lab-header__menu-icon" /> Users
                </Dropdown.Item>
              )}

              {canAccess("/Tabs") && (
                <Dropdown.Item as={Link} to="/Tabs" className="lab-header__menu-item">
                  <TbScreenShare className="lab-header__menu-icon" /> Lab Details
                </Dropdown.Item>
              )}

              {canAccess("/") && (
                <Dropdown.Item
                  disabled
                  className="lab-header__menu-item lab-header__menu-item--disabled"
                >
                  <PiHandbagSimple className="lab-header__menu-icon" /> Store
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </div>
  );
}

export default Header;